import axios from "axios";
import { computed } from "vue";
import CommonHelper from "./CommonHelper.js";
import FirebaseService from "../service/FirebaseService.js";
import EmailService from "../service/EmailService.js";

/*
 * Determines whether or not to use the default user data
 * Returns true if one of two conditions are met:
 * 1. location is in authorized locations
 * 2. referrer is in authorized referrers and query search is being run
 */
const useDefaultUserData = computed(() => {
  var returnValue = process.env.VUE_APP_USE_DEFAULT_USER_DATA === "true";

  if (!returnValue && process.env.VUE_APP_AUTHORIZED_REFERRERS != null && process.env.VUE_APP_AUTHORIZED_REFERRERS != "") {
    if (CommonHelper.querySearchType.value.includes("singular") || CommonHelper.querySearchType.value.includes("advanced")) {
      const referrer = document.referrer;

      const authorizedReferrers = process.env.VUE_APP_AUTHORIZED_REFERRERS.split(",");

      authorizedReferrers.forEach((authorizedReferrer) => {
        if (referrer.includes(authorizedReferrer)) {
          returnValue = true;
        }
      });
    }
  }

  return returnValue;
});

// Determines if the current referrer is from an authorized referrer
const currentAuthorizedReferrer = computed(() => {
  let returnValue = null;
  const referrer = document.referrer;

  if (useDefaultUserData.value && referrer !== "" && process.env.VUE_APP_USE_DEFAULT_USER_DATA === "false") {
    if (process.env.VUE_APP_AUTHORIZED_REFERRERS_CPT.split(",").some(authorizedReferrer => referrer.includes(authorizedReferrer))) {
      returnValue = "cpt";
    } else if (process.env.VUE_APP_AUTHORIZED_REFERRERS_MAXIMO.split(",").some(authorizedReferrer => referrer.includes(authorizedReferrer))) {
      returnValue = "maximo";
    }
  }

  return returnValue;
})

function setWithExpiry(key, value, minutes) {
  const now = new Date();
  const ttl = 60000 * minutes;

  // `item` is an object which contains the original value
  // as well as the time when it's supposed to expire
  const item = {
    value: value,
    expiry: now.getTime() + ttl,
  };

  localStorage.setItem(key, JSON.stringify(item));
}

function getWithExpiry(key) {
  const itemStr = localStorage.getItem(key);

  // if the item doesn't exist, return null
  if (!itemStr) {
    return null;
  }

  const item = JSON.parse(itemStr);
  const now = new Date();

  // compare the expiry time of the item with the current time
  if (now.getTime() > item.expiry) {
    // If the item is expired, delete the item from storage
    // and return null
    localStorage.removeItem(key);
    return null;
  }

  return item.value;
}

async function getIpAddress() {
  try {
    const response = await axios.get("https://api.ipify.org/?format=json");
    return response.data.ip;
  } catch (error) {
    if (!error.toString().includes("Network Error")) {
      EmailService.sendErrorEmail("AuthHelper.js - getIpAddress", error);
    }
    return "";
  }
}

function getUid() {
  var error403Count = 0;

  while (error403Count < 50) {
    try {
      const { user } = FirebaseService.getUser();
      const uid = user.value.uid;
      setWithExpiry("currentLoggedInUserUid", uid, 30);
      return uid;
    } catch (error) {
      if (error.toString().includes("403")) {
        error403Count++;
      } else {
        if (!error.toString().includes("Cannot read properties of null (reading 'uid')")) {
          EmailService.sendErrorEmail("AuthHelper.js - getUid", error);
        }
        localStorage.removeItem("currentLoggedInUserUid");
        FirebaseService.firebaseSignOut();
        throw error;
      }
    }
  }

  return getWithExpiry("currentLoggedInUserUid");
}

async function fetchUserWithID() {
  const uid = getUid();
  const res = await axios.get(`${process.env.VUE_APP_API_GATEWAY}getUserByUid?uid=${uid}&key=${process.env.VUE_APP_API_KEY}`);
  return res;
}

async function getUserGroups(email) {
  const res = await CommonHelper.getUserGroups(false);

  return res.filter((userGroup) => {
    return userGroup.emails !== "" && userGroup.emails.split(",").includes(email);
  });
}

async function getCurrentUserData(includeScopesAccess, includeAccessLevel) {
  let returnValue = {};

  try {
    const currentUserData = await fetchUserWithID();

    returnValue["uid"] = currentUserData.data.uid;
    returnValue["email"] = currentUserData.data.email;
    returnValue["name"] = currentUserData.data.displayName;

    if (includeScopesAccess) {
      const scopesAccess = currentUserData.data.customClaims?.scopesAccess;

      returnValue["scopesAccess"] = scopesAccess !== undefined && scopesAccess !== "" ? scopesAccess.split(",") : [];

      const userGroups = await getUserGroups(currentUserData.data.email);

      userGroups.forEach((userGroup) => {
        if (userGroup.scopesAccess !== undefined && userGroup.scopesAccess !== "") {
          const scopesAccessArray = userGroup.scopesAccess.split(",");

          scopesAccessArray.forEach((scopesAccess) => {
            if (!returnValue["scopesAccess"].includes(scopesAccess)) {
              returnValue["scopesAccess"].push(scopesAccess);
            }
          });
        }
      });

      returnValue["scopesAccess"].sort();
    }

    if (includeAccessLevel) {
      const accessLevel = currentUserData.data.customClaims?.accessLevel;
  
      returnValue["accessLevel"] = accessLevel !== undefined && accessLevel !== "" ? accessLevel.split(",") : [];
  
      const userGroups = await getUserGroups(currentUserData.data.email);
  
      userGroups.forEach((userGroup) => {
        if (userGroup.accessLevel !== undefined && userGroup.accessLevel !== "") {
          const accessLevelArray = userGroup.accessLevel.split(",");
  
          accessLevelArray.forEach((accessLevel) => {
            returnValue["accessLevel"].push(accessLevel);
          });
        }
      });
    }

    return returnValue; 
  } catch (error) {
    if (process.env.VUE_APP_USE_DEFAULT_USER_DATA === "true") {
      returnValue["uid"] = process.env.VUE_APP_DEFAULT_USER_UID;
      returnValue["email"] = "facd-smtp-fwd@eworldes.com";
      returnValue["name"] = "FacDocs Admin";

      if (includeAccessLevel) {
        if (process.env.VUE_APP_DEFAULT_USER_ACCESS_LEVEL !== undefined && process.env.VUE_APP_DEFAULT_USER_ACCESS_LEVEL !== "") {
          returnValue["accessLevel"] = process.env.VUE_APP_DEFAULT_USER_ACCESS_LEVEL.split(",");
        } else {
          returnValue["accessLevel"] = [];
        }
      }
    } else {
      if (!error.toString().includes("Cannot read properties of null (reading 'uid')")) {
        EmailService.sendErrorEmail("AuthHelper.js - getCurrentUserData", error);
      }
      returnValue["uid"] = "";
      returnValue["email"] = "";
      returnValue["name"] = "";
      returnValue["accessLevel"] = [];
    }

    if (includeScopesAccess) {
      if (currentAuthorizedReferrer.value === "maximo") {
        returnValue["scopesAccess"] = process.env.VUE_APP_DEFAULT_USER_SCOPES_ACCESS_MAXIMO.split(",");
      } else if (currentAuthorizedReferrer.value === "cpt") {
        returnValue["scopesAccess"] = process.env.VUE_APP_DEFAULT_USER_SCOPES_ACCESS_CPT.split(",");
      } else if (process.env.VUE_APP_USE_DEFAULT_USER_DATA === "true") {
        if (process.env.VUE_APP_DEFAULT_USER_SCOPES_ACCESS !== undefined && process.env.VUE_APP_DEFAULT_USER_SCOPES_ACCESS !== "") {
          returnValue["scopesAccess"] = process.env.VUE_APP_DEFAULT_USER_SCOPES_ACCESS.split(",");
        } else {
          returnValue["scopesAccess"] = [];
        }
      } else {
        returnValue["scopesAccess"] = [];
      }
    }

    return returnValue;
  }
}

export default {
  getIpAddress,
  getCurrentUserData
};
